import React, { useState, useEffect } from 'react';
import { X, Check, Crown, Loader, ExternalLink } from 'lucide-react';
import { getPricingPlans, createCheckoutSession, getSubscriptionStatus, upgradeSubscription, PricingPlan, SubscriptionStatus } from '../services/stripeService';
import { isNativeApp } from '../utils/platform';

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PricingModal: React.FC<PricingModalProps> = ({ isOpen, onClose }) => {
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentSubscription, setCurrentSubscription] = useState<SubscriptionStatus | null>(null);
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadPricing();
      loadSubscriptionStatus();
    }
  }, [isOpen]);

  const loadPricing = async () => {
    try {
      setLoading(true);
      setError(null);
      const pricingPlans = await getPricingPlans();
      setPlans(pricingPlans);
    } catch (err: any) {
      console.error('Failed to load pricing:', err);
      setError('Failed to load pricing plans. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionStatus = async () => {
    try {
      const status = await getSubscriptionStatus();
      setCurrentSubscription(status);
    } catch (err: any) {
      // User might not be logged in or have no subscription - that's ok
      console.log('Could not load subscription status:', err);
      setCurrentSubscription(null);
    }
  };

  const handleUpgrade = async (planType: 'pro' | 'plus') => {
    try {
      setCheckoutLoading(planType);
      setError(null);
      setUpgradeSuccess(false);

      // Check if user has an active subscription (not free tier)
      const hasActiveSubscription = currentSubscription &&
        currentSubscription.subscription_tier !== 'free' &&
        currentSubscription.subscription_status === 'active' &&
        currentSubscription.stripe_subscription_id;

      if (hasActiveSubscription) {
        // User has existing subscription - use upgrade with proration
        await upgradeSubscription(planType);
        setUpgradeSuccess(true);
        // Refresh subscription status
        await loadSubscriptionStatus();
        // Show success message briefly then close
        setTimeout(() => {
          onClose();
          window.location.reload(); // Refresh to update UI with new tier
        }, 2000);
      } else {
        // No subscription - create new checkout session
        await createCheckoutSession(planType);
        // User will be redirected to Stripe Checkout
      }
    } catch (err: any) {
      console.error('Upgrade/Checkout error:', err);
      setError(err.message || 'Failed to process upgrade');
      setCheckoutLoading(null);
    }
  };

  if (!isOpen) return null;

  // Don't show pricing modal in native mobile apps (Apple App Store compliance)
  if (isNativeApp()) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-sm sm:max-w-md md:max-w-3xl lg:max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Choose Your Plan</h2>
            <p className="text-gray-600 text-sm mt-1">Select the plan that fits your needs</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 py-6 sm:py-8">
          {upgradeSuccess ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Upgrade Successful!</h3>
              <p className="text-gray-600 text-center">
                Your subscription has been upgraded with prorated billing.
                <br />
                Refreshing your account...
              </p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-8 w-8 animate-spin text-purple-600" />
              <span className="ml-3 text-gray-600">Loading pricing...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={loadPricing}
                className="btn-primary"
              >
                Try Again
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {plans.map((plan) => {
                const isPopular = plan.id === 'pro';
                const isPlus = plan.id === 'plus';

                return (
                <div
                  key={plan.id}
                  className={`rounded-lg border-2 p-6 relative ${
                    isPopular
                      ? 'border-blue-500 bg-blue-50 shadow-lg'
                      : isPlus
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full text-xs font-bold">
                      MOST POPULAR
                    </div>
                  )}

                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {plan.name}
                    </h3>
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-gray-900">
                        ${plan.price}
                      </span>
                      <span className="text-gray-500 ml-2">/{plan.interval}</span>
                    </div>
                    {plan.savings && (
                      <p className="text-sm text-green-600 font-medium mt-1">
                        {plan.savings}
                      </p>
                    )}
                  </div>

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {(() => {
                    const isCurrentPlan = currentSubscription?.subscription_tier === plan.id;
                    const hasActiveSubscription = currentSubscription &&
                      currentSubscription.subscription_tier !== 'free' &&
                      currentSubscription.subscription_status === 'active';
                    const isFreePlan = plan.id === 'free';
                    const isCurrentFree = !currentSubscription || currentSubscription.subscription_tier === 'free';

                    if (isCurrentPlan) {
                      return (
                        <button
                          disabled
                          className="w-full py-2 px-4 rounded-lg bg-green-100 text-green-700 cursor-not-allowed font-medium"
                        >
                          Current Plan
                        </button>
                      );
                    }

                    if (isFreePlan) {
                      return (
                        <button
                          disabled
                          className="w-full py-2 px-4 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                        >
                          {isCurrentFree ? 'Current Plan' : 'Free Tier'}
                        </button>
                      );
                    }

                    return (
                      <button
                        onClick={() => {
                          const planType = plan.id === 'plus' ? 'plus' : 'pro';
                          handleUpgrade(planType);
                        }}
                        disabled={!!checkoutLoading}
                        className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                          plan.id === 'pro'
                            ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-lg text-white'
                            : plan.id === 'plus'
                            ? 'bg-purple-600 hover:bg-purple-700 text-white'
                            : 'bg-purple-600 hover:bg-purple-700 text-white'
                        } disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
                      >
                        {checkoutLoading === plan.id ? (
                          <>
                            <Loader className="h-4 w-4 animate-spin mr-2" />
                            {isNativeApp() ? 'Opening website...' : 'Processing...'}
                          </>
                        ) : (
                          <>
                            {isNativeApp() ? (
                              <ExternalLink className="h-4 w-4 mr-2" />
                            ) : (
                              <Crown className="h-4 w-4 mr-2" />
                            )}
                            {hasActiveSubscription
                              ? `Upgrade to ${plan.name}${isNativeApp() ? '' : ' (Prorated)'}`
                              : `${isNativeApp() ? 'Subscribe on Website' : `Upgrade to ${plan.name}`}`}
                          </>
                        )}
                      </button>
                    );
                  })()}
                </div>
              )})}
            </div>
          )}

          {/* Additional info */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500">
              {isNativeApp()
                ? 'You will be redirected to our website to complete your purchase securely.'
                : 'All payments are securely processed by Stripe. Cancel anytime.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingModal;